import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { backendClient } from '@/lib/backendClient';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

interface PractitionerEarnings {
  referral_code: string;
  code_status: 'pending' | 'active' | 'revoked' | 'suspended';
}

export const useReferralCode = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [copying, setCopying] = useState(false);

  // Fetch practitioner's referral code from the new commission system
  const {
    data: referralData,
    isLoading,
    error
  } = useQuery({
    queryKey: ['practitioner-referral-code', user?.id],
    queryFn: async () => {
      if (!user) return null;

      try {
        // Fetch from the practitioner earnings endpoint which includes the referral code
        const apiBaseUrl = import.meta.env.VITE_BACKEND_URL || '/api/v1';
        const response = await fetch(`${apiBaseUrl}/referrals/practitioner/earnings`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${(await backendClient.auth.getSession()).data.session?.access_token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          // If 404, practitioner doesn't have a referral code yet
          if (response.status === 404) {
            return null;
          }
          throw new Error(`Failed to fetch referral code: ${response.statusText}`);
        }

        const data: PractitionerEarnings = await response.json();
        return data;
      } catch (err) {
        console.error('Error fetching referral code:', err);
        throw err;
      }
    },
    enabled: !!user,
    retry: false, // Don't retry if practitioner doesn't have a code
  });

  // Copy referral link to clipboard
  const copyReferralLink = async () => {
    if (!referralData?.referral_code) return;

    setCopying(true);
    const referralLink = `${window.location.origin}/signup?ref=${referralData.referral_code}`;

    try {
      await navigator.clipboard.writeText(referralLink);
      toast({
        title: "Copied!",
        description: "Referral link copied to clipboard",
      });
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = referralLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);

      toast({
        title: "Copied!",
        description: "Referral link copied to clipboard",
      });
    } finally {
      setCopying(false);
    }
  };

  const referralCode = referralData?.referral_code || null;
  const codeStatus = referralData?.code_status || null;
  const referralLink = referralCode ? `${window.location.origin}/signup?ref=${referralCode}` : null;

  return {
    referralCode,
    referralLink,
    codeStatus,
    isLoading,
    error,
    copying,
    copyReferralLink,
    hasNoCode: !isLoading && !referralCode && !error, // Practitioner doesn't have a code yet
  };
};